<template>
  <div class="ride__stops" data-test="ride-stops-container">
    <div class="ride__stop d-flex align-center pb-6" data-test="departure-stop">
      <div class="ride__stop-time-info mr-1">
        <span
          v-if="!rideDetails.isDepartureExactTime"
          class="ride__stop-time textPrimary--text body-1 mr-1"
          data-test="departure-time-approx"
          >~</span
        >
        <span class="ride__stop-time textPrimary--text body-1" data-test="departure-time">{{
          timeDeparture
        }}</span>
      </div>
      <div
        class="ride__stop-number white--text mr-1"
        :class="`ride__stop-number ${orderAsc ? 'asc' : 'desc'}--${
          orderAsc ? 1 : 2
        }`"
        data-test="departure-marker"
      >
        <VIcon :color="orderAsc ? 'brandingPrimary' : 'secondary'" large>
          mdi-map-marker
        </VIcon>
      </div>
      <div class="ride__stop-info d-flex flex-column h-100 pl-1">
        <span class="ride__stop-name textPrimary--text" data-test="departure-name">{{
          formatTitle(rideDetails.outward)
        }}</span>
        <span
          v-if="rideDetails.outward.isPoi || rideDetails.outward.poiName"
          class="ride__stop-name-info mb-2 textTertiary--text caption"
          data-test="departure-poi-info"
        >
          {{ formatStopSecondLine(rideDetails.outward) }}
        </span>
      </div>
    </div>
    <div class="ride__stop d-flex align-center" data-test="arrival-stop">
      <div class="ride__stop-time-info mr-1">
        <span
          v-if="rideDetails.isDepartureExactTime"
          class="ride__stop-time textPrimary--text body-1 mr-1"
          data-test="arrival-time-approx"
          >~
        </span>
        <span class="ride__stop-time textPrimary--text body-1" data-test="arrival-time">
          {{ timeArrival }}</span
        >
        <div v-if="isTripForTwoDays" data-test="arrival-date-container">
          <span class="ride__stop-time textPrimary--text body-1" data-test="arrival-date">{{
            getDateArrival
          }}</span>
        </div>
      </div>
      <div
        class="ride__stop-number white--text mr-1"
        :class="`ride__stop-number ${orderAsc ? 'asc' : 'desc'}--${
          orderAsc ? 2 : 1
        }`"
        data-test="arrival-marker"
      >
        <VIcon :color="orderAsc ? 'secondary' : 'brandingPrimary'" large>
          mdi-map-marker
        </VIcon>
      </div>
      <div class="ride__stop-info d-flex flex-column h-100 pl-1">
        <span class="ride__stop-name textPrimary--text" data-test="arrival-name">{{
          formatTitle(rideDetails.arrival)
        }}</span>
        <span
          v-if="rideDetails.arrival.isPoi || rideDetails.arrival.poiName"
          class="ride__stop-name-info mb-2 textTertiary--text caption"
          data-test="arrival-poi-info"
        >
          {{ formatStopSecondLine(rideDetails.arrival) }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'RideStops',
};
</script>

<script setup lang="ts">
import { computed } from 'vue';
import BookingStopTypeEnum from '@slg/web-customer-shared/src/enum/BookingStopTypeEnum';
import STimeConverter from '@slg/web-customer-shared/src/utils/timeConverter';

const props = defineProps({
  rideDetails: {
    type: Object,
    default: () => {},
  },
  orderAsc: {
    type: Boolean,
    default: true,
  },
});

const timeDeparture = computed(() => (props.rideDetails.bookingStopType ===
  BookingStopTypeEnum.ARRIVAL_TIME
  ? STimeConverter.convertMilliSecondsToTimeZoneTime(
    props.rideDetails.dateState.time - props.rideDetails.timeInMinutes,
    props.rideDetails.timeZone
  )
  : STimeConverter.convertMilliSecondsToTimeZoneTime(
    props.rideDetails.dateState.time,
    props.rideDetails.timeZone
  )));

const timeArrival = computed(() => (props.rideDetails.bookingStopType ===
  BookingStopTypeEnum.ARRIVAL_TIME
  ? STimeConverter.convertMilliSecondsToTimeZoneTime(
    props.rideDetails.dateState.time,
    props.rideDetails.timeZone
  )
  : STimeConverter.convertMilliSecondsToTimeZoneTime(
    props.rideDetails.dateState.time + props.rideDetails.timeInMinutes,
    props.rideDetails.timeZone
  )));

const getDateArrival = computed(() => (props.rideDetails.bookingStopType ===
  BookingStopTypeEnum.ARRIVAL_TIME
  ? STimeConverter.convertMilliSecondsToTimeZoneMonthDayFormatWithoutComma(
    props.rideDetails.dateState.time
  )
  : STimeConverter.convertMilliSecondsToTimeZoneMonthDayFormatWithoutComma(
    props.rideDetails.dateState.time + props.rideDetails.timeInMinutes
  )));

const isTripForTwoDays = computed(() => {
  const firstStopDate =
    props.rideDetails.bookingStopType === BookingStopTypeEnum.ARRIVAL_TIME
      ? STimeConverter.convertMilliSecondsToTimeZoneMonthDayFormatWithoutComma(
        props.rideDetails.dateState.time - props.rideDetails.timeInMinutes
      )
      : STimeConverter.convertMilliSecondsToTimeZoneMonthDayFormatWithoutComma(
        props.rideDetails.dateState.time
      );
  return firstStopDate !== getDateArrival.value;
});

function formatStopSecondLine(stop: { poiStopName?: string; address?: string }) {
  return `${stop.poiStopName} ${stop.poiStopName ? '-' : ''} ${
    stop.address
  }`;
}

function formatTitle(stop: { isPoi?: boolean; poiName?: string; address?: string }) {
  return `${stop.isPoi ? stop.poiName : stop.address}`;
}
</script>

<style lang="scss" scoped>
.ride {
  &__stop {
    overflow: hidden;
    .ride__stop-number {
      &:after,
      &:before {
        content: "";
        position: absolute;
        left: 50%;
        transform: translate(-50%, 0);
        border-left: 2px dashed #b5b5b5;
      }
    }
  }
  &__stop-time-info {
    width: 60px;
    flex: 0 0 auto;
  }
  &__stop-time {
    display: inline-block;
  }
  &__stop-time-distance {
    display: inline-block;
  }
  &__stop-number {
    position: relative;
    width: 23px;
    height: 32px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    &.asc--1 {
      &:after {
        bottom: -9999px;
        top: 100%;
      }
    }
    &.asc--2 {
      &:before {
        top: -9999px;
        bottom: 100%;
      }
    }
    &.desc--1 {
      &:after {
        top: -9999px;
        bottom: 100%;
      }
    }
    &.desc--2 {
      &:before {
        bottom: -9999px;
        top: 100%;
      }
    }
  }
  &__stop-name {
    display: inline-block;
  }
  &__stop-name-info,
  &__stop-country {
    display: inline-block;
  }
}
</style>
